import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { RootState } from 'data/root.reducers';
import { CollectionFunctionGroup, CollectionFunctionGroupGridData, CollectionFunctionGroupPrivileges } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { AuditDetails, GridSettings, WithAuditDetails } from 'data/types';
import * as selectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as userActions from 'users/users-actions';
import { adaptToGridData } from 'data/collectionFunctionGroup/collectionFunctionGroup.adapter';
import { AppToaster } from 'shared/toaster';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import CollectionFunctionGroupGrid from './components/CollectionFunctionGroupGrid';
import { collectionFunctionGroupAutoGroupColumnDef, collectionFunctionGroupColumnDefs, collectionFunctionGroupColumnDefsVersion } from '../../CollectionFunctionGroup.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  beforeLogout: authenticationSelectors.getAuthenticationBeforeLogout(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  data: selectors.getCollectionFunctionGroupList(state),
  selectedCollectionFunctionGroup: selectors.getSelectedCollectionFunctionGroup(state),
});

const mapDispatch = {
  getCollectionFunctionGroups: actions.getCollectionFunctionGroupList,
  goToCollectionGroupCreate: actions.goToCollectionGroupCreate,
  goToCollectionGroupEdit: actions.goToCollectionGroupEdit,
  selectCollectionFunctionGroup: actions.selectCollectionFunctionGroup,
  deleteCollectionFunctionGroup: actions.deleteCollectionFunctionGroup,
  getGridSettings: userActions.getUserSettings,
  saveGridSettings: userActions.putUserSettings,
  goToCollectionFunctionGroupView: actions.goToCollectionFunctionGroupView,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

const USER_CATEGORY_SETTINGS = 'CollectionFunctionGroupSettings';

export const CollectionFunctionGroupList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  privileges,
  deliveryFunctionId,
  data = [],
  beforeLogout,
  selectedCollectionFunctionGroup,
  history,
  getCollectionFunctionGroups,
  getGridSettings,
  saveGridSettings,
  goToCollectionGroupCreate,
  goToCollectionGroupEdit,
  selectCollectionFunctionGroup,
  deleteCollectionFunctionGroup,
  goToCollectionFunctionGroupView,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [collectionFunctionGroupGridData, setCollectionFunctionGroupGridData] =
    useState<CollectionFunctionGroupGridData[]>([]);

  useEffect(() => {
    getCollectionFunctionGroups(deliveryFunctionId);

    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getCollectionFunctionGroups,
  ]);

  useEffect(() => {
    setLastUpdatedAt(new Date());

    const adaptedData = adaptToGridData(data);

    setCollectionFunctionGroupGridData(adaptedData);
  }, [data]);

  const handleRefresh = () => getCollectionFunctionGroups(deliveryFunctionId);

  const handleCreateNew = () => goToCollectionGroupCreate(history);

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedRows: CollectionFunctionGroupGridData[] = event.api.getSelectedRows();

    const selectedCollectionFunctionGroups = selectedRows.map(({ parentId }) => {
      return data.find(({ id }) => id === parentId);
    });

    selectCollectionFunctionGroup(selectedCollectionFunctionGroups as CollectionFunctionGroup[]);
  };

  const handleGetGridSettings = () => getGridSettings(USER_CATEGORY_SETTINGS, userName);

  const handleSaveGridSettings = (gridSettings: GridSettings) =>
    saveGridSettings(USER_CATEGORY_SETTINGS, userName, gridSettings);

  const handleCollectionFunctionGroupEdit = () => goToCollectionGroupEdit(history);

  const handleGoToCollectionFunctionGroup = () => goToCollectionFunctionGroupView(history);

  const handleCollectionFunctionGroupDelete = () => {
    const collectionFunctionGroup = selectedCollectionFunctionGroup[0];

    if (collectionFunctionGroup?.id == null) {
      return;
    }

    const collectionFunctionGroupBody: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        collectionFunctionGroup.name,
        AuditService.CF_GROUP,
        AuditActionType.COLLECTION_FUNCTION_GROUP_DELETE,
      );
      collectionFunctionGroupBody.auditDetails = auditDetails as AuditDetails;
    }

    deleteCollectionFunctionGroup(deliveryFunctionId, collectionFunctionGroup.id, collectionFunctionGroupBody);
  };

  const hasPrivilegesToCreate = privileges.includes(CollectionFunctionGroupPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(CollectionFunctionGroupPrivileges.Delete);

  return (
    <div data-testid="CollectionFunctionGroupList" style={{ height: 'inherit' }}>
      <CollectionFunctionGroupGrid
        selectedCollectionFunctionGroup={selectedCollectionFunctionGroup?.[0]}
        data={collectionFunctionGroupGridData}
        columnDefs={collectionFunctionGroupColumnDefs}
        columnDefsVersion={collectionFunctionGroupColumnDefsVersion}
        autoGroupColumnDef={collectionFunctionGroupAutoGroupColumnDef}
        noResults={false}
        beforeLogout={beforeLogout}
        lastUpdatedAt={lastUpdatedAt}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onGetGridSettings={handleGetGridSettings}
        onSaveGridSettings={handleSaveGridSettings}
        onSelectionChanged={handleSelectionChanged}
        onEdit={handleCollectionFunctionGroupEdit}
        onDelete={handleCollectionFunctionGroupDelete}
        onSelectItemMenuAction={handleGoToCollectionFunctionGroup}
        suppressAggFuncInHeader
        groupSelectsChildren
        suppressRowClickSelection
        enableGroupSelection
      />
    </div>
  );
};

export default withRouter(connector(CollectionFunctionGroupList));
